// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
// Preuve de travail de la connexion au back-office.
//
// Elle empêche un tiers qui ne connaît que l'adresse d'un administrateur de
// remplir le plafond des réseaux inconnus du compte et de lui fermer le
// back-office. Au-delà du premier échec, le dashboard joint une preuve : défi
// signé et daté servi par GET /api/public/defi, nonce tel que
// SHA-256(« sel:nonce ») commence par `bits` bits nuls, envoyé dans l'en-tête
// X-Vigeos-Preuve. La tentative compte alors sur le plafond des preuves du
// compte, que les échecs sans preuve ne remplissent pas
// (backend/app/services/shared/preuve_travail.py, staff_auth).
//
// Sans dépendance (ni React, ni axios) : même solveur que vigeos.fr, la console
// et l'app Business, joué contre le serveur sous node par un test du backend.

// Navigateur : une tranche de 20 000 essais dure quelques millisecondes.
const CHUNK_SIZE = 20000;

var K256 = new Int32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2]);
var H256 = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
  0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];

// Un bloc de 64 octets : `w[0..15]` rempli par l'appelant, `h` mis à jour.
function sha256Block(h, w) {
  var t, x, y;
  for (t = 16; t < 64; t++) {
    x = w[t - 15]; y = w[t - 2];
    w[t] = (w[t - 16] + (((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3))
      + w[t - 7] + (((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10))) | 0;
  }
  var a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], k = h[7];
  for (t = 0; t < 64; t++) {
    var t1 = (k + (((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7)))
      + ((e & f) ^ (~e & g)) + K256[t] + w[t]) | 0;
    var t2 = ((((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10)))
      + ((a & b) ^ (a & c) ^ (b & c))) | 0;
    k = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
  }
  h[0] = (h[0] + a) | 0; h[1] = (h[1] + b) | 0; h[2] = (h[2] + c) | 0; h[3] = (h[3] + d) | 0;
  h[4] = (h[4] + e) | 0; h[5] = (h[5] + f) | 0; h[6] = (h[6] + g) | 0; h[7] = (h[7] + k) | 0;
}

// SHA-256 d'une chaîne (UTF-8), en hexadécimal. Sert aux tests du solveur.
export function sha256Hex(text) {
  var brut = unescape(encodeURIComponent(String(text)));
  var n = brut.length, blocks = ((n + 8) >> 6) + 1, words = new Array(blocks * 16);
  var i;
  for (i = 0; i < words.length; i++) words[i] = 0;
  for (i = 0; i < n; i++) words[i >> 2] |= brut.charCodeAt(i) << (24 - (i & 3) * 8);
  words[n >> 2] |= 0x80 << (24 - (n & 3) * 8);
  words[blocks * 16 - 1] = n * 8;
  var h = new Int32Array(H256), w = new Int32Array(64);
  for (var b = 0; b < blocks; b++) {
    for (i = 0; i < 16; i++) w[i] = words[b * 16 + i];
    sha256Block(h, w);
  }
  var out = '';
  for (i = 0; i < 8; i++) out += ('00000000' + (h[i] >>> 0).toString(16)).slice(-8);
  return out;
}

// Premier nonce de [debut, fin) tel que SHA-256(« sel:nonce ») commence par
// `bits` bits nuls, ou null. Le message tient dans un seul bloc (sel de 24
// caractères, nonce de 16 chiffres au plus) ; les chiffres du nonce
// s'incrémentent sur place, sans chaîne à fabriquer à chaque essai.
export function findNonce(sel, bits, debut, end) {
  var h = new Int32Array(8), w = new Int32Array(64), bytes = new Int32Array(64);
  var i, n, pos, prefix = String(sel) + ':';
  var digits = String(debut).split('');
  for (i = 0; i < prefix.length; i++) bytes[i] = prefix.charCodeAt(i) & 0xff;
  for (n = debut; n < end; n++) {
    var l = prefix.length + digits.length;
    for (i = 0; i < digits.length; i++) bytes[prefix.length + i] = digits[i].charCodeAt(0);
    bytes[l] = 0x80;
    for (i = l + 1; i < 64; i++) bytes[i] = 0;
    for (i = 0; i < 15; i++) {
      w[i] = (bytes[4 * i] << 24) | (bytes[4 * i + 1] << 16) | (bytes[4 * i + 2] << 8) | bytes[4 * i + 3];
    }
    w[15] = l * 8;
    for (i = 0; i < 8; i++) h[i] = H256[i];
    sha256Block(h, w);
    var zeroBits = h[0] === 0 ? 32 + Math.clz32(h[1]) : Math.clz32(h[0]);
    if (zeroBits >= bits) return String(n);
    // nonce + 1, chiffre par chiffre
    for (pos = digits.length - 1; pos >= 0 && digits[pos] === '9'; pos--) digits[pos] = '0';
    if (pos < 0) digits.unshift('1'); else digits[pos] = String.fromCharCode(digits[pos].charCodeAt(0) + 1);
  }
  return null;
}

const ceder = () => new Promise((ok) => { setTimeout(ok, 0); });

// Au-delà de ce délai, on renonce : la requête part sans preuve, sur le
// budget partagé, plutôt que de faire attendre (téléphone lent, difficulté
// relevée par VIGEOS_PREUVE_BITS). Le défi vit 180 s côté serveur.
const DELAY_MS = 20000;

// Le nonce du défi, cherché par tranches de `chunkSize` essais entre lesquelles
// la main est rendue (l'interface ne gèle pas), ou null au-delà de `limit`
// essais ou de `delayMs` millisecondes.
export async function resolve(sel, bits,
  { tranche: chunkSize = CHUNK_SIZE, limite: limit = 0x40000000, delaiMs: delayMs = DELAY_MS } = {}) {
  const end = Date.now() + delayMs;
  for (let debut = 0; debut < limit; debut += chunkSize) {
    const n = findNonce(sel, bits, debut, Math.min(debut + chunkSize, limit));
    if (n !== null) return n;
    if (Date.now() > end) return null;
    await ceder();
  }
  return null;
}

export const PROOF_HEADER = 'X-Vigeos-Preuve';

// « défi:nonce » pour `scope`, ou null si quoi que ce soit échoue (défi
// injoignable, serveur sans défi) : la requête part alors sans preuve, sur le
// budget partagé. `readChallenge(scope)` rend le corps du défi.
export async function getProof(readChallenge, scope) {
  try {
    const d = await readChallenge(scope);
    if (!d || typeof d.defi !== 'string' || typeof d.sel !== 'string') return null;
    const n = await resolve(d.sel, Number(d.bits));
    return n === null ? null : `${d.defi}:${n}`;
  } catch {
    return null;
  }
}

const statusOf = (e) => e?.response?.status ?? e?.status;

// Connexion avec preuve au-delà du premier échec : le premier essai part sans
// preuve (pas d'attente pour qui tape juste) ; après un refus (401 ou 429)
// pour cette adresse, les essais suivants en portent une ; un 429 reçu sans
// preuve se rejoue une fois, aussitôt, avec une preuve, puisque le verrou
// venait peut-être du budget partagé qu'un tiers a rempli.
//   send(preuve) : l'appel de connexion, avec l'en-tête si `proof` ;
//   proof() : `getProof` déjà liée à la portée « connexion » ;
//   echecs : Set des adresses refusées, tenu par l'appelant.
export async function loginWithProof({ cle: key, echecs: failures, envoyer: send, preuve: proof }) {
  const address = String(key || '').trim().toLowerCase();
  let valeur = failures.has(address) ? await proof() : null;
  let res;
  try {
    res = await send(valeur);
  } catch (e) {
    const statusValue = statusOf(e);
    if (statusValue === 401 || statusValue === 429) failures.add(address);
    if (statusValue !== 429 || valeur) throw e;
    valeur = await proof();
    if (!valeur) throw e;
    res = await send(valeur);
  }
  failures.delete(address);
  return res;
}
